// Patients.jsx - PERFECT table-integrated tabs [file:60]

import React, { useEffect, useState } from 'react';
import Header from '../Header/Hearder';
import './Patients.css';
import axiosInstance from "../../lib/axiosInstance";
import { buildPatientListQueryParams } from "../../lib/patientListSearchParams";

const Patients = ({ branches = [], selectedBranchId = "", onBranchChange }) => {
  const maxDobDate = new Date().toISOString().split("T")[0];
  const [searchTerm, setSearchTerm] = useState("");
  const PAGE_SIZE = 10;
  const [activeTab, setActiveTab] = useState("All Patients");
  const [patients, setPatients] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPatients, setTotalPatients] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [historyPatient, setHistoryPatient] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [doctorNameById, setDoctorNameById] = useState({});
  const [form, setForm] = useState({
    name: "",
    registrationNumber: "",
    age: "",
    email: "",
    phone: "",
    dateOfBirth: "",
    gender: "",
    address: "",
    emergencyContact: { name: "", phone: "" },
    medicalHistory: [{ condition: "", diagnosisDate: "", notes: "" }],
    isActive: true,
  });

  function getDefaultForm() {
    return {
      name: "",
      registrationNumber: "",
      age: "",
      email: "",
      phone: "",
      dateOfBirth: "",
      gender: "",
      address: "",
      emergencyContact: { name: "", phone: "" },
      medicalHistory: [{ condition: "", diagnosisDate: "", notes: "" }],
      isActive: true,
    };
  }

  function openCreatePatient() {
    setEditingId(null);
    setError("");
    setForm(getDefaultForm());
    setShowForm(true);
  }

  function closePatientForm() {
    setShowForm(false);
    setError("");
    setEditingId(null);
    setForm(getDefaultForm());
  }

  const fetchPatients = async () => {
    if (!selectedBranchId) {
      setPatients([]);
      setTotalPatients(0);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const params = {
        branchId: selectedBranchId,
        page: currentPage,
        limit: PAGE_SIZE,
        _ts: Date.now(),
        ...buildPatientListQueryParams(searchTerm),
      };
      if (activeTab === "Active") params.isActive = true;
      const res = await axiosInstance.get("/patients", { params });
      const data = res && res.data ? res.data : res;
      // server returns { items, total, page, limit }
      if (data && data.items) {
        setPatients(data.items);
        setTotalPatients(typeof data.total === "number" ? data.total : data.items.length);
      } else if (Array.isArray(data)) {
        setPatients(data);
        setTotalPatients(data.length);
      } else {
        setPatients([]);
        setTotalPatients(0);
      }
    } catch (err) {
      setError((err && err.message) || "Failed to load patients");
    } finally {
      setLoading(false);
    }
  };

 
 
  // refetch when filters/page changes (debounced)
  useEffect(() => {
    const t = setTimeout(() => {
      fetchPatients();
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, searchTerm, selectedBranchId, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, selectedBranchId]);

  // Load doctor names for visit history display (branch-scoped)
  useEffect(() => {
    let mounted = true;
    async function loadDoctorNames() {
      if (!selectedBranchId) {
        if (mounted) setDoctorNameById({});
        return;
      }
      try {
        const res = await axiosInstance.get("/doctor-branches", { params: { branchId: selectedBranchId, isActive: true } });
        const data = res && res.data ? res.data : res;
        const maps = Array.isArray(data) ? data : (data.items || []);
        const list = maps.map((m) => m.doctorId).filter(Boolean);
        const map = {};
        list.forEach((d) => {
          const id = d && (d._id || d.id);
          if (id) map[String(id)] = d.name || d.fullName || String(id);
        });
        if (mounted) setDoctorNameById(map);
      } catch (e) {
        if (mounted) setDoctorNameById({});
      }
    }
    loadDoctorNames();
    return () => { mounted = false; };
  }, [selectedBranchId]);

  function getDoctorNameById(doctorVal) {
    if (!doctorVal) return "-";
    if (typeof doctorVal === "object") return doctorVal.name || doctorVal.fullName || (doctorVal._id || doctorVal.id) || "-";
    const id = String(doctorVal);
    return doctorNameById[id] || id;
  }

  useEffect(() => {
    const maybeOpenCreate = () => {
      const params = new URLSearchParams(window.location.search || "");
      if (params.get("create") === "1") {
        openCreatePatient();
        // clean URL after opening modal
        window.history.replaceState({}, "", "/patientrecords");
      }
    };
    maybeOpenCreate();
    window.addEventListener("popstate", maybeOpenCreate);
    return () => window.removeEventListener("popstate", maybeOpenCreate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function validatePatientForm(values) {
    const name = String(values.name || "").trim();
    const phone = String(values.phone || "").trim();
    const email = String(values.email || "").trim();
    const dob = String(values.dateOfBirth || "").trim();
    const gender = String(values.gender || "").trim();
    const ageVal = values.age;
    const emergencyName = String(values.emergencyContact?.name || "").trim();
    const emergencyPhone = String(values.emergencyContact?.phone || "").trim();
    const allowedGenders = ["male", "female", "other"];

    if (!name || name.length < 2) return "Please enter a valid patient name (min 2 characters).";
    if (!phone) return "Phone number is required.";
    if (!/^\d{10}$/.test(phone)) return "Phone number must be exactly 10 digits.";
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return "Please enter a valid email address.";
    if (dob && new Date(dob) > new Date()) return "Date of birth cannot be in the future.";
    if (gender && !allowedGenders.includes(gender)) return "Please select a valid gender.";

    // Optional: age (if provided)
    if (ageVal !== "" && ageVal != null) {
      const ageNum = Number(ageVal);
      if (!Number.isFinite(ageNum) || ageNum < 0 || ageNum > 150) {
        return "Please enter a valid age (0 to 150).";
      }
    }

    if ((emergencyName && !emergencyPhone) || (!emergencyName && emergencyPhone)) {
      return "Emergency contact requires both name and phone.";
    }
    if (emergencyPhone && !/^\d{10}$/.test(emergencyPhone)) {
      return "Emergency contact phone must be exactly 10 digits.";
    }

    if (Array.isArray(values.medicalHistory)) {
      for (const mh of values.medicalHistory) {
        const condition = String(mh?.condition || "").trim();
        const diagnosisDate = String(mh?.diagnosisDate || "").trim();
        const notes = String(mh?.notes || "").trim();
        const hasAny = condition || diagnosisDate || notes;
        if (!hasAny) continue;
        if (!condition) return "Medical history condition is required when adding history details.";
      }
    }

    return "";
  }

  function getBranchNameById(branchVal) {
    const branchId = branchVal && typeof branchVal === "object" ? (branchVal._id || branchVal.id) : branchVal;
    if (!branchId) return "-";
    const b = (branches || []).find((x) => (x._id || x.id) === branchId);
    return b ? (b.name || b.branchName || branchId) : String(branchId);
  }

  function extractPatientFromDetailsResponse(resLike) {
    const raw = resLike && resLike.data !== undefined ? resLike.data : resLike;
    if (!raw) return null;
    if (raw.patient && typeof raw.patient === "object") return raw.patient;
    if (raw.data && typeof raw.data === "object" && !Array.isArray(raw.data)) return raw.data;
    return raw;
  }

  /** Flatten odd API shapes and coerce strings for form + details modal. */
  function normalizePatientDetails(p) {
    if (!p || typeof p !== "object") return {};
    const ecRaw = p.emergencyContact;
    const ec =
      ecRaw && typeof ecRaw === "object" && !Array.isArray(ecRaw)
        ? ecRaw
        : { name: "", phone: "" };

    const phoneStr =
      p.phone != null && p.phone !== ""
        ? String(p.phone).replace(/\D/g, "").slice(0, 10)
        : "";

    let ageNorm = "";
    if (p.age !== "" && p.age != null) {
      const n = Number(p.age);
      if (Number.isFinite(n)) ageNorm = n;
    }

    const dobRaw = p.dateOfBirth;
    const dobStr =
      dobRaw == null || dobRaw === ""
        ? ""
        : typeof dobRaw === "string"
          ? dobRaw.split("T")[0]
          : "";

    const mhIn = Array.isArray(p.medicalHistory) ? p.medicalHistory : [];
    const medicalHistory =
      mhIn.length > 0
        ? mhIn.map((mh) => ({
            condition: mh && mh.condition != null ? String(mh.condition) : "",
            diagnosisDate:
              mh && mh.diagnosisDate
                ? typeof mh.diagnosisDate === "string"
                  ? mh.diagnosisDate.split("T")[0]
                  : mh.diagnosisDate
                : "",
            notes: mh && mh.notes != null ? String(mh.notes) : "",
          }))
        : [{ condition: "", diagnosisDate: "", notes: "" }];

    return {
      ...p,
      name: String(p.name ?? "").trim(),
      phone: phoneStr,
      email: p.email != null && p.email !== "" ? String(p.email) : "",
      registrationNumber:
        p.registrationNumber != null && p.registrationNumber !== ""
          ? String(p.registrationNumber)
          : "",
      age: ageNorm === "" ? "" : ageNorm,
      dateOfBirth: dobStr,
      gender: p.gender ? String(p.gender) : "",
      address: p.address != null && p.address !== "" ? String(p.address) : "",
      emergencyContact: {
        name: ec.name != null ? String(ec.name) : "",
        phone:
          ec.phone != null && ec.phone !== ""
            ? String(ec.phone).replace(/\D/g, "").slice(0, 10)
            : "",
      },
      medicalHistory,
      visitHistory: Array.isArray(p.visitHistory) ? p.visitHistory : [],
      billingHistory: Array.isArray(p.billingHistory) ? p.billingHistory : [],
      isActive: typeof p.isActive === "boolean" ? p.isActive : p.isActive !== false,
      createdAt: p.createdAt,
      updatedAt: p.updatedAt,
    };
  }

  function patientApiToFormState(p) {
    const n = normalizePatientDetails(p);
    return {
      name: n.name || "",
      registrationNumber: n.registrationNumber || "",
      age: n.age === "" || n.age == null ? "" : n.age,
      email: n.email || "",
      phone: n.phone || "",
      dateOfBirth: n.dateOfBirth || "",
      gender: n.gender || "",
      address: n.address || "",
      emergencyContact: {
        name: n.emergencyContact?.name || "",
        phone: n.emergencyContact?.phone || "",
      },
      medicalHistory:
        n.medicalHistory && n.medicalHistory.length
          ? n.medicalHistory.map((mh) => ({
              condition: mh.condition || "",
              diagnosisDate: mh.diagnosisDate || "",
              notes: mh.notes || "",
            }))
          : [{ condition: "", diagnosisDate: "", notes: "" }],
      isActive: !!n.isActive,
    };
  }

  function formatEmergencyLine(ec) {
    if (!ec || typeof ec !== "object") return "-";
    const n = String(ec.name || "").trim();
    const ph = String(ec.phone || "").trim();
    if (!n && !ph) return "-";
    if (n && ph) return `${n} (${ph})`;
    return n || ph;
  }

  function extractBillItemsFromResponse(resLike) {
    const raw = resLike && resLike.data !== undefined ? resLike.data : resLike;
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (Array.isArray(raw.items)) return raw.items;
    if (Array.isArray(raw.data)) return raw.data;
    if (Array.isArray(raw.bills)) return raw.bills;
    if (Array.isArray(raw.results)) return raw.results;
    return [];
  }

  function mapBillsToBillingHistory(bills, pid) {
    return (Array.isArray(bills) ? bills : [])
      .filter((b) => {
        const patientVal = b && (b.patientId || b.patient || b.patientRef);
        if (!patientVal) return true; // keep if API already filtered by patient
        const patientId = typeof patientVal === "object" ? (patientVal._id || patientVal.id) : patientVal;
        return !pid || String(patientId || "") === String(pid);
      })
      .map((b) => ({
        billId: b._id || b.id || b.billId,
        billNumber: b.billNumber || b.invoiceNumber || "",
        status: b.status || "paid",
        totalAmount: Number(b.totalAmount || b.amount || 0),
        paidAt: b.paidAt || b.billDate || b.createdAt || null,
        branchId: b.branchId,
      }));
  }
  const totalPages = Math.max(1, Math.ceil(totalPatients / PAGE_SIZE));

  return (
    <div className="patients-page">
      <Header branches={branches} selectedBranchId={selectedBranchId} onBranchChange={onBranchChange} />
      
      {/* MAIN CONTENT CONTAINER */}
      <div className="patients-main">
        {/* PAGE HEADER */}
        <div className="page-header2">
          <div className="header-left">
            <h1>Patient Records</h1>
            <p>Manage patient data, history, and records</p>
          </div>
          <div className="header-right">
            <div className="search-container">
              <input
                className="search-input"
                placeholder="Search patient by name"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
            <button
              type="button"
              className="add-patient-btn"
              onClick={openCreatePatient}
            >
              + Add New Patient
            </button>
          </div>
        </div>

        {/* PATIENTS TABLE WITH INTEGRATED TABS */}
        <div className="patients-table-container">
          {/* TABS INSIDE TABLE HEADER */}
          <div className="table-tabs">
            <div 
              className={`tab ${activeTab === 'All Patients' ? 'active' : ''}`}
              onClick={() => setActiveTab('All Patients')}
            >
              All Patients
            </div>
            <div 
              className={`tab ${activeTab === 'Active' ? 'active' : ''}`}
              onClick={() => setActiveTab('Active')}
            >
              Active
            </div>
          </div>

          {showForm && (
            <div
              className="modal-overlay"
              onClick={closePatientForm}
            >
              <div
                className="modal-content"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <h3>New patient</h3>
                {error && <div className="form-error">{error}</div>}
                <div className="form-grid">
                  <input
                    placeholder="Full name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                  <input
                    placeholder="Registration No"
                    value={form.registrationNumber}
                    onChange={(e) => setForm({ ...form, registrationNumber: e.target.value })}
                  />
                  <input
                    placeholder="Phone"
                    value={form.phone}
                    maxLength={10}
                    inputMode="numeric"
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
                      setForm({ ...form, phone: digits });
                    }}
                  />
                  <input
                    type="text"
                    inputMode="email"
                    autoComplete="email"
                    placeholder="Email (optional)"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                  <input
                    type="number"
                    placeholder="Age"
                    min={0}
                    value={form.age}
                    onChange={(e) => {
                      const raw = e.target.value;
                      // keep empty string allowed so user can clear the field
                      if (raw === "") {
                        setForm({ ...form, age: "" });
                        return;
                      }
                      const n = Number(raw);
                      setForm({ ...form, age: Number.isFinite(n) ? n : "" });
                    }}
                  />
                  <input
                    type={form.dateOfBirth ? "date" : "text"}
                    placeholder="DOB (optional)"
                    max={maxDobDate}
                    value={form.dateOfBirth}
                    onFocus={(e) => {
                      e.target.type = "date";
                    }}
                    onBlur={(e) => {
                      if (!e.target.value) e.target.type = "text";
                    }}
                    onChange={(e) => setForm({ ...form, dateOfBirth: e.target.value })}
                  />
                  <select value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })}>
                    <option value="">Select gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>

                  <label className="active-checkbox">
                    <input
                      type="checkbox"
                      checked={!!form.isActive}
                      onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                    />
                    Active
                  </label>

                  <input
                    placeholder="Emergency contact name"
                    value={form.emergencyContact.name}
                    onChange={(e) =>
                      setForm({ ...form, emergencyContact: { ...form.emergencyContact, name: e.target.value } })
                    }
                  />
                  <input
                    placeholder="Emergency contact phone"
                    value={form.emergencyContact.phone}
                    maxLength={10}
                    inputMode="numeric"
                    onChange={(e) =>
                      setForm({
                        ...form,
                        emergencyContact: {
                          ...form.emergencyContact,
                          phone: e.target.value.replace(/\D/g, "").slice(0, 10),
                        },
                      })
                    }
                  />

                  <textarea
                    placeholder="Address"
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                  />

                  {/* Medical history entries */}
                  <div className="medical-history" style={{ gridColumn: "1 / -1" }}>
                    <label className="medical-history-label">Medical history</label>
                    {form.medicalHistory.map((mh, idx) => (
                      <div key={idx} className="history-row">
                        <input
                          className="history-condition"
                          placeholder="Condition"
                          value={mh.condition}
                          onChange={(e) => {
                            const next = [...form.medicalHistory];
                            next[idx] = { ...next[idx], condition: e.target.value };
                            setForm({ ...form, medicalHistory: next });
                          }}
                        />
                        <input
                          className="history-date"
                          type="date"
                          value={mh.diagnosisDate ? mh.diagnosisDate.split("T")[0] : ""}
                          onChange={(e) => {
                            const next = [...form.medicalHistory];
                            next[idx] = { ...next[idx], diagnosisDate: e.target.value };
                            setForm({ ...form, medicalHistory: next });
                          }}
                        />
                        <div className="history-notes-wrap">
                          <input
                            className="history-notes-input"
                            placeholder="Notes"
                            value={mh.notes}
                            onChange={(e) => {
                              const next = [...form.medicalHistory];
                              next[idx] = { ...next[idx], notes: e.target.value };
                              setForm({ ...form, medicalHistory: next });
                            }}
                          />
                          <button
                            type="button"
                            className="remove-history-btn"
                            onClick={() => {
                              const next = form.medicalHistory.filter((_, i) => i !== idx);
                              setForm({ ...form, medicalHistory: next.length ? next : [{ condition: "", diagnosisDate: "", notes: "" }] });
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="add-history-btn"
                      onClick={() =>
                        setForm({ ...form, medicalHistory: [...form.medicalHistory, { condition: "", diagnosisDate: "", notes: "" }] })
                      }
                    >
                      + Add history
                    </button>
                  </div>
                </div>
                <div className="form-actions">
                      <button
                        type="button"
                        className="create-btn"
                        onClick={async () => {
                          setError("");
                          if (!selectedBranchId) {
                            setError("Please select a branch first.");
                            return;
                          }
                          const validationError = validatePatientForm(form);
                          if (validationError) {
                            setError(validationError);
                            return;
                          }
                          try {
                            if (!editingId) {
                              const emailNorm = String(form.email || "").trim().toLowerCase();
                              const duplicate = emailNorm
                                ? (patients || []).find((p) => {
                                    const pEmail = String(p.email || "").trim().toLowerCase();
                                    return pEmail === emailNorm;
                                  })
                                : null;
                              if (duplicate) {
                                setError("A patient with this email already exists.");
                                return;
                              }
                            }
                            // Normalize payload to match backend expectations
                            const payload = {
                              name: form.name,
                              registrationNumber: String(form.registrationNumber || "").trim() || undefined,
                              age:
                                form.age === "" || form.age == null
                                  ? undefined
                                  : Number(form.age),
                              email: String(form.email || "").trim() || undefined,
                              phone: form.phone,
                              dateOfBirth: String(form.dateOfBirth || "").trim() || undefined,
                              gender: form.gender || undefined,
                              address: form.address || undefined,
                              emergencyContact: form.emergencyContact && (Object.keys(form.emergencyContact).length ? form.emergencyContact : undefined),
                              medicalHistory:
                                Array.isArray(form.medicalHistory) && form.medicalHistory.length
                                  ? form.medicalHistory.map((mh) => ({
                                      condition: mh.condition || "",
                                      diagnosisDate: mh.diagnosisDate || undefined,
                                      notes: mh.notes || "",
                                    }))
                                  : undefined,
                              branchId: selectedBranchId,
                              isActive: typeof form.isActive !== "undefined" ? !!form.isActive : undefined,
                            };

                            if (editingId) {
                              await axiosInstance.put(`/patients/${editingId}`, payload);
                            } else {
                              await axiosInstance.post("/patients", payload);
                            }
                            closePatientForm();
                            await fetchPatients();
                          } catch (err) {
                            setError((err && err.message) || "Failed to create patient");
                          }
                        }}
                      >
                        {editingId ? "Update patient" : "Create patient"}
                      </button>
                  <button type="button" className="cancel-btn" onClick={closePatientForm}>Cancel</button>
                </div>
              </div>
            </div>
          )}

          {showHistory && (
            <div
              className="modal-overlay"
              onClick={() => {
                setShowHistory(false);
                setHistoryPatient(null);
              }}
            >
              <div
                className="modal-content history-modal-content"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <h3>Patient Details</h3>
                <div className="history-modal-body">
                  {historyLoading ? (
                    <div>Loading...</div>
                  ) : historyPatient ? (
                    <div className="detail-grid">
                    <div className="detail-row"><strong>Name:</strong> {historyPatient.name || "-"}</div>
                    <div className="detail-row"><strong>Registration no.:</strong> {historyPatient.registrationNumber || "-"}</div>
                    <div className="detail-row"><strong>Age:</strong> {historyPatient.age !== "" && historyPatient.age != null ? historyPatient.age : "-"}</div>
                    <div className="detail-row"><strong>Email:</strong> {historyPatient.email || "-"}</div>
                    <div className="detail-row"><strong>Phone:</strong> {historyPatient.phone || "-"}</div>
                    <div className="detail-row"><strong>DOB:</strong> {historyPatient.dateOfBirth ? new Date(historyPatient.dateOfBirth).toLocaleDateString() : "-"}</div>
                    <div className="detail-row"><strong>Gender:</strong> {historyPatient.gender || "-"}</div>
                    <div className="detail-row"><strong>Address:</strong> {historyPatient.address || "-"}</div>
                    <div className="detail-row"><strong>Emergency:</strong> {formatEmergencyLine(historyPatient.emergencyContact)}</div>
                    <div className="detail-row"><strong>Status:</strong> <span className={`badge ${historyPatient.isActive ? "active" : "inactive"}`}>{historyPatient.isActive ? "Active" : "Inactive"}</span></div>
                    <div className="detail-row"><strong>Created:</strong> {historyPatient.createdAt ? new Date(historyPatient.createdAt).toLocaleString() : "-"}</div>
                    <div className="detail-row"><strong>Updated:</strong> {historyPatient.updatedAt ? new Date(historyPatient.updatedAt).toLocaleString() : "-"}</div>
                    <div className="detail-row" style={{ gridColumn: "1 / -1" }}>
                      <strong>Medical history:</strong>
                      {Array.isArray(historyPatient.medicalHistory) && historyPatient.medicalHistory.length ? (
                        <ul className="mh-list">
                          {historyPatient.medicalHistory.map((mh, i) => {
                            const cond = String(mh?.condition || "").trim();
                            const notes = String(mh?.notes || "").trim();
                            const hasDate = !!(mh?.diagnosisDate && String(mh.diagnosisDate).trim());
                            const dateStr = hasDate ? new Date(mh.diagnosisDate).toLocaleDateString() : "-";
                            const emptySlot = !cond && !notes && !hasDate;
                            return (
                              <li key={i}>
                                <div>
                                  <strong>{cond || "—"}</strong> — {dateStr}
                                </div>
                                {notes ? (
                                  <div className="mh-notes">{notes}</div>
                                ) : emptySlot ? (
                                  <div className="mh-notes">No details recorded for this slot.</div>
                                ) : null}
                              </li>
                            );
                          })}
                        </ul>
                      ) : (
                        <div>-</div>
                      )}
                    </div>
                    <div className="detail-row" style={{ gridColumn: "1 / -1" }}>
                      <strong>Visit history:</strong>
                      {Array.isArray(historyPatient.visitHistory) && historyPatient.visitHistory.length ? (
                        <ul className="mh-list">
                          {historyPatient.visitHistory
                            .slice()
                            .sort((a, b) => new Date(b.visitDate || b.createdAt || 0) - new Date(a.visitDate || a.createdAt || 0))
                            .map((v, i) => (
                              <li key={`${v.appointmentId || "visit"}-${i}`}>
                                <div>
                                  <strong>{v.visitDate ? new Date(v.visitDate).toLocaleString() : "-"}</strong>
                                  {" — "}
                                  <span>{String(v.status || "scheduled").toUpperCase()}</span>
                                  {v.source ? ` (${v.source})` : ""}
                                </div>
                                <div className="mh-notes">
                                  Doctor: {getDoctorNameById(v.doctorId)} | Branch: {getBranchNameById(v.branchId)}
                                </div>
                              </li>
                            ))}
                        </ul>
                      ) : (
                        <div>-</div>
                      )}
                    </div>
                    <div className="detail-row" style={{ gridColumn: "1 / -1" }}>
                      <strong>Billing history:</strong>
                      {Array.isArray(historyPatient.billingHistory) && historyPatient.billingHistory.length ? (
                        <ul className="mh-list">
                          {historyPatient.billingHistory
                            .slice()
                            .sort((a, b) => new Date(b.paidAt || b.createdAt || 0) - new Date(a.paidAt || a.createdAt || 0))
                            .map((b, i) => (
                              <li key={`${b.billId || b.billNumber || "bill"}-${i}`}>
                                <div>
                                  <strong>{b.billNumber || b.billId || "Bill"}</strong>
                                  {" — "}
                                  <span>{String(b.status || "paid").toUpperCase()}</span>
                                </div>
                                <div className="mh-notes">
                                  Amount: ₹{Number(b.totalAmount || 0).toLocaleString()} | Paid At: {b.paidAt ? new Date(b.paidAt).toLocaleString() : "-"} | Branch: {getBranchNameById(b.branchId)}
                                </div>
                              </li>
                            ))}
                        </ul>
                      ) : (
                        <div>-</div>
                      )}
                    </div>
                    </div>
                  ) : (
                    <div>No details available.</div>
                  )}
                </div>
                <div style={{ marginTop: 16, textAlign: "right" }}>
                  <button type="button" className="create-btn" onClick={() => setShowHistory(false)}>Close</button>
                </div>
              </div>
            </div>
          )}

          <div className="patients-table-scroll">
            <table className="patients-table">
              <thead>
                <tr>
                  <th>Patient Name</th>
                  <th>Registration No</th>
                  <th>Phone Number</th>
                  <th>Last Visit</th>
                  <th>Quick Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="5">Loading...</td>
                  </tr>
                ) : patients.length === 0 ? (
                  <tr>
                    <td colSpan="5">No patients found.</td>
                  </tr>
                ) : (
                  // filter by active tab client-side as a safety net
                  patients
                    .filter((p) => (activeTab === "Active" ? p.isActive !== false : true))
                    .map((patient) => {
                      const pid = patient._id || patient.id;
                      return (
                        <tr key={pid}>
                          <td>
                            <div className="patient-row">
                              <div className="patient-avatar1" style={{ backgroundColor: patient.color || "#ddd" }}>
                                {(patient.name || "").split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase()}
                              </div>
                              <span className="patient-name">{patient.name}</span>
                            </div>
                          </td>
                          <td>{patient.registrationNumber || "-"}</td>
                          <td>{patient.phone}</td>
                          <td>{patient.lastVisit || (patient.updatedAt ? new Date(patient.updatedAt).toLocaleDateString() : "")}</td>
                          <td className="actions-cell">
                        <button
                          type="button"
                          className="history-btn"
                          onClick={async () => {
                            setShowHistory(true);
                            setHistoryLoading(true);
                            try {
                              const res = await axiosInstance.get(`/patients/${pid}`, { params: { branchId: selectedBranchId } });
                              const raw = extractPatientFromDetailsResponse(res) || {};
                              const basePatient = normalizePatientDetails(raw);
                              const existingBilling = Array.isArray(basePatient.billingHistory) ? basePatient.billingHistory : [];
                              let mergedPatient = basePatient;
                              // Some backends omit billingHistory in patient details; fetch bills as fallback.
                              if (!existingBilling.length) {
                                try {
                                  const billsRes = await axiosInstance.get("/bills", {
                                    params: { branchId: selectedBranchId, patientId: pid, page: 1, limit: 500, _ts: Date.now() },
                                  });
                                  const billItems = extractBillItemsFromResponse(billsRes);
                                  const mappedHistory = mapBillsToBillingHistory(billItems, pid);
                                  mergedPatient = normalizePatientDetails({
                                    ...basePatient,
                                    billingHistory: mappedHistory,
                                  });
                                } catch (_) {
                                  mergedPatient = normalizePatientDetails({
                                    ...basePatient,
                                    billingHistory: [],
                                  });
                                }
                              }
                              setHistoryPatient(mergedPatient);
                            } catch (err) {
                              setHistoryPatient(null);
                              alert((err && err.message) || "Failed to load patient details");
                              setShowHistory(false);
                            } finally {
                              setHistoryLoading(false);
                            }
                          }}
                        >
                          🕒 History
                        </button>

                          <div
                            className="row-menu-wrap"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              type="button"
                              className="row-menu-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenMenuId(openMenuId === pid ? null : pid);
                              }}
                            >
                              ⋯
                            </button>
                            {openMenuId === pid && (
                              <div className="row-menu">
                                <button
                                  type="button"
                                  className="row-menu-item"
                                  onClick={async () => {
                                    setOpenMenuId(null);
                                    setError("");
                                    try {
                                      const res = await axiosInstance.get(`/patients/${pid}`, {
                                        params: { branchId: selectedBranchId },
                                      });
                                      const raw = extractPatientFromDetailsResponse(res) || {};
                                      setForm(patientApiToFormState(raw));
                                      setEditingId(pid);
                                      setShowForm(true);
                                    } catch (err) {
                                      setForm(patientApiToFormState(patient));
                                      setEditingId(pid);
                                      setShowForm(true);
                                      setError((err && err.message) || "Could not load full patient; showing list row data.");
                                    }
                                  }}
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  className="row-menu-item delete"
                                  onClick={async () => {
                                    setOpenMenuId(null);
                                    if (!window.confirm("Permanently delete this patient record? This cannot be undone.")) return;
                                    try {
                                      await axiosInstance.delete(`/patients/${pid}`);
                                      await fetchPatients();
                                    } catch (err) {
                                      alert((err && err.message) || "Delete failed");
                                    }
                                  }}
                                >
                                  Delete
                                </button>
                              </div>
                            )}
                          </div>
                          </td>
                        </tr>
                      );
                    })
                )}
              </tbody>
            </table>
          </div>
          <div className="patients-pagination">
            <div className="patients-pagination-info">
              Page {Math.min(currentPage, totalPages)} of {totalPages} {totalPatients ? `(${totalPatients} records)` : ""}
            </div>
            <div className="patients-pagination-actions">
              <button
                type="button"
                className="page-btn"
                disabled={currentPage <= 1 || loading}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <button
                type="button"
                className="page-btn"
                disabled={currentPage >= totalPages || loading}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Patients;
